import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getKeyByValue } from 'utils';
import { appConstants, endpoints } from 'utils/global-constants';
import { Button } from 'components/UI/Button/Button';
import axios from 'axios';
import { filterDeliveryMethods } from './booking-delivery-methods-utils';
import { DeliveryMethodsAccordion } from './DeliveryMethodsAccordion/DeliveryMethodsAccordion';
import { DeliveryMethodMobileTicket } from './DeliveryMethodsTypesContent/DeliveryMethodMobileTicket';
import { DeliveryMethodVenueCollection } from './DeliveryMethodsTypesContent/DeliveryMethodVenueCollection';
import { DeliveryMethodETicket } from './DeliveryMethodsTypesContent/DeliveryMethodETicket';
import { DeliveryMethodPostalAddress } from './DeliveryMethodsTypesContent/DeliveryMethodPostalAddress';
import './BookingDeliveryMethods.scss';

export const BookingDeliveryMethods = (props) => {
  const {
    SelectedDeliveryMethodID,
    DeliveryMethods,
    DeliveryMethodDetails,
    FaqsForMTPopUp,
    FaqsForETPopUp,
    ShowMoreTextMT,
    ShowMoreTextET,
    SelectedAddress,
    Addresses,
    States,
    NewAddress,
    Order,
  } = props;
  const ctaButtonTextDefault = 'Review cart & make payment';
  const [selectedDeliveryMethodId, setSelectedDeliveryMethodId] =
    useState(SelectedDeliveryMethodID);
  const [ctaButtonText, setCtaButtonText] = useState(ctaButtonTextDefault);
  const [ctaButtonDisabled, setCtaButtonDisabled] = useState(true);
  const [apiResponseError, setApiResponseError] = useState('');

  // if there is no delivery address (e.g. new user via progressive checkout), default to Venue collection
  const venueCollectionAddress = {
    ShippingMethod: -1,
    AddressId: 0,
    FullAddress: 'Venue Collection',
  };
  const defaultSelectedAddress = SelectedAddress || Addresses[0] || venueCollectionAddress;
  const [selectedAddress, setSelectedAddress] = useState(defaultSelectedAddress);

  /**
   * Filter the DeliveryMethodDetails based from the available DeliveryMethods.
   * Sort by DeliveryMethodId, desc
   *  */
  const sortingDeliveryList = [21, 18, 1, 3, 4, -1];

  const filteredDeliveryMethods = filterDeliveryMethods(
    DeliveryMethods,
    DeliveryMethodDetails
  ).sort(
    (a, b) =>
      sortingDeliveryList.indexOf(a.DeliveryMethodId) -
      sortingDeliveryList.indexOf(b.DeliveryMethodId)
  );

  useEffect(() => {
    setCtaButtonDisabled(!selectedDeliveryMethodId);
  }, [selectedDeliveryMethodId]);

  const handleRadioButtonChange = (radioButtonId) => {
    setSelectedDeliveryMethodId(radioButtonId);
    setCtaButtonDisabled(false);
  };

  const handleReviewCartButtonClick = () => {
    setCtaButtonText('Processing...');
    setCtaButtonDisabled(true);

    const apiUrl = endpoints.processDelivery;

    const payload = {
      deliveryData: {
        SelectedDeliveryMethodID: selectedDeliveryMethodId.toString(),
        SelectedAddress: { ...selectedAddress },
      },
    };

    axios
      .post(apiUrl, payload)
      .then((response) => {
        trackEvent('add_shipping_info', { Order, payload });
        if (response.data.Redirect) {
          window.location.href = response.data?.RedirectUrl;
        }

        if (response.data.Error) {
          setApiResponseError(response.data.Error);
          setCtaButtonText(ctaButtonTextDefault);
          setCtaButtonDisabled(false);
        }
      })
      .catch(() => {
        setApiResponseError('Something went wrong. Please try again.');
      });
  };

  return (
    <div data-testid="delivery-methods" className="delivery-methods">
      <h2 className="h4 cart-content-heading" data-testid="section-title">
        How would you like to receive your tickets?
      </h2>

      <DeliveryMethodsAccordion id="accordionDelivery" extendClass="delivery-options">
        {filteredDeliveryMethods.map((deliveryMethod, index) => {
          const deliveryMethodId = deliveryMethod.DeliveryMethodId;
          const deliveryMethodType = getKeyByValue(
            appConstants.DeliveryMethodType,
            deliveryMethodId
          );

          const args = {
            ...deliveryMethod,
            selectedDeliveryMethodId,
            handleRadioButtonChange,
          };

          let deliveryMethodContent;
          switch (deliveryMethodType) {
            case 'CompanionCard':
            case 'MobileTicket':
              deliveryMethodContent = (
                <DeliveryMethodMobileTicket
                  {...args}
                  faqsForMTPopUp={FaqsForMTPopUp}
                  showMoreTextMT={ShowMoreTextMT}
                  key={index}
                />
              );
              break;
            case 'ETicket':
              deliveryMethodContent = (
                <DeliveryMethodETicket
                  {...args}
                  faqsForETPopUp={FaqsForETPopUp}
                  showMoreTextET={ShowMoreTextET}
                  key={index}
                />
              );
              break;
            case 'VenueCollection':
              deliveryMethodContent = (
                <DeliveryMethodVenueCollection {...args} key={deliveryMethodId} />
              );
              break;
            case 'ExpressPost':
            case 'RegisteredPost':
            case 'RegularPost':
              deliveryMethodContent = (
                <DeliveryMethodPostalAddress
                  {...args}
                  addresses={Addresses}
                  selectedAddress={selectedAddress}
                  states={States}
                  key={deliveryMethodId}
                  newAddress={NewAddress}
                  setSelectedAddress={setSelectedAddress}
                />
              );
              break;
            default:
              deliveryMethodContent = '';
          }

          return deliveryMethodContent;
        })}

        {apiResponseError && (
          <div className="callout-row">
            <div className="callout-container callout-error">
              <p>{apiResponseError}</p>
            </div>
          </div>
        )}
      </DeliveryMethodsAccordion>

      <Button
        onClick={handleReviewCartButtonClick}
        text={ctaButtonText}
        variant="primary"
        disabled={ctaButtonDisabled}
        extendTestId="cta"
      />

      <div className="text-complement">
        <p>
          Need to find more tickets? <br className="visible-xs" />
          <a href="/whats-on" className="link-bold">
            Continue browsing events
          </a>
        </p>
      </div>
    </div>
  );
};

export const BookingDeliveryMethodsSC = (props) => {
  const { sitecoreData } = props;
  return <BookingDeliveryMethods {...sitecoreData} />;
};

BookingDeliveryMethodsSC.propTypes = {
  sitecoreData: PropTypes.object,
};

BookingDeliveryMethods.propTypes = {
  DeliveryMethods: PropTypes.arrayOf(PropTypes.object),
  DeliveryMethodDetails: PropTypes.arrayOf(PropTypes.object),
  FaqsForMTPopUp: PropTypes.object,
  FaqsForETPopUp: PropTypes.object,
  ShowMoreTextMT: PropTypes.string,
  ShowMoreTextET: PropTypes.string,
  SelectedAddress: PropTypes.object,
  Addresses: PropTypes.array,
  States: PropTypes.array,
  NewAddress: PropTypes.object,
  SelectedDeliveryMethodID: PropTypes.number,
};
